import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { appMonthName, appMonthNameYear } from '@/utils/appDateFormat';
import StatusBadge from '../../../../components/ui/StatusBadge';
import { Card, CardContent } from '../../../../components/ui/Card';
import { WpsCoverageView, WpsDeadlineView } from '../../../../models/payroll';
import { LawReference } from '../../ComplianceCenter/components/LawReference';
import { WPS_TONE_BADGE, useMohreCitation, useWpsLabels, wpsZoneTone } from './wpsLabels';

interface MonthStatusLineProps {
  year: number;
  month: number;
  deadline: WpsDeadlineView;
  coverage: WpsCoverageView | null;
}

export const ClockOffNote: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground"
      data-testid="wps-clock-off-note"
    >
      <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <p>
        {t('payroll.wps.uae.clockOff.note')}{' '}
        <Link to="/settings/payroll/compliance" className="font-medium underline underline-offset-2 hover:no-underline">
          {t('payroll.wps.uae.clockOff.change')}
        </Link>
      </p>
    </div>
  );
};

/** The month in one read: where it stands, how long since the due date, and the share figure. Not a pass badge. */
export const MonthStatusLine: React.FC<MonthStatusLineProps> = ({ year, month, deadline, coverage }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const citation = useMohreCitation();
  const monthName = appMonthNameYear(new Date(year, month - 1));
  const status = deadline.monthStatus;
  const submitted = deadline.submittedCount ?? 0;
  const outstanding = deadline.outstandingCount ?? 0;
  const threshold = coverage?.thresholdPercent ?? deadline.thresholdPercent ?? 85;
  const held = deadline.heldNames ?? [];
  const heldMore = Math.max(0, (deadline.heldCount ?? 0) - held.length);
  const answeredElsewhere = (deadline.closedCount ?? 0) + (deadline.outsideCount ?? 0);

  const statusText = (() => {
    switch (status) {
      case 'Submitted': return t('payroll.wps.uae.status.submitted');
      case 'SubmittedBelowShare': return t('payroll.wps.uae.status.belowShare', { threshold });
      case 'PartlySubmitted': return t('payroll.wps.uae.status.partly', { submitted, total: submitted + outstanding });
      case 'NotSubmitted': return t('payroll.wps.uae.status.notSubmitted');
      case 'NothingFiled': return t('payroll.wps.uae.status.nothingFiled', { month: monthName, count: answeredElsewhere });
      case 'NoEmployees': return t('payroll.wps.uae.status.noEmployees');
      default: return '';
    }
  })();

  const days = deadline.daysAfterDue ?? 0;
  // The 1st of the next month, for every company size. Worked out here so a timezone can never move the legal date.
  const dueDate = `1 ${appMonthName(new Date(year, month, 1), 'short')}`;
  const dueText = (() => {
    if (days > 0) return t('payroll.wps.uae.due.after', { date: dueDate, count: days });
    if (days === 0) return t('payroll.wps.uae.due.today', { date: dueDate });
    return t('payroll.wps.uae.due.before', { date: dueDate, count: -days });
  })();

  const shareText = (() => {
    if (!coverage || status === 'NoEmployees') return '';
    if (coverage.hasLegacyFile || coverage.sharePercent == null) return t('payroll.wps.uae.share.unavailable');
    return t('payroll.wps.uae.share.figure', { share: coverage.sharePercent, threshold });
  })();

  const tone = wpsZoneTone(deadline.zone, status);

  return (
    <Card>
      <CardContent className="p-4 space-y-2" data-testid="wps-month-status">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0 space-y-1">
            <p className="font-semibold text-foreground" data-testid="wps-month-status-text" data-status={status ?? ''}>
              {statusText}
              {status === 'NothingFiled' && <> {t('payroll.wps.uae.status.notExemption')}</>}
            </p>
            {held.length > 0 && (
              <p className="text-sm text-foreground" data-testid="wps-held-names">
                {t('payroll.wps.uae.status.held', { names: labels.names(held, heldMore) })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusBadge variant={WPS_TONE_BADGE[tone]} size="sm" data-testid="wps-zone" data-zone={deadline.zone}>
              {labels.zone(deadline.zone)}
            </StatusBadge>
            <LawReference citations={[citation]} />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
          {!deadline.clockOff && <span data-testid="wps-due-text">{dueText}</span>}
          {shareText && <span data-testid="wps-share-text">{shareText}</span>}
        </div>
        {(coverage?.shareUnavailableCount ?? 0) > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="wps-share-unavailable">
            {t('payroll.wps.uae.share.leftOut', { n: coverage?.shareUnavailableCount ?? 0 })}
          </p>
        )}
        <div className="space-y-0.5 text-xs text-muted-foreground">
          <p>{t('payroll.wps.uae.law.dueDate')}</p>
          <p>{t('payroll.wps.uae.law.share')}</p>
        </div>
      </CardContent>
    </Card>
  );
};
